import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Star, Package, ArrowLeft, Minus, Plus } from "lucide-react";
import api from "@/lib/api";
import { useStore } from "@/lib/store";
import { DeliveryBadge, inr } from "@/components/badges";

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const { addToCart } = useStore();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setQty(r.data.moq || 1);
    }).catch(() => {});
  }, [id]);

  if (!p) return <p className="py-16 text-center text-sm text-slate-500" data-testid="product-loading">Loading…</p>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="product-page">
      <Link to="/buyer" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-900" data-testid="back-to-shop">
        <ArrowLeft className="h-4 w-4" /> Back to catalog
      </Link>
      <div className="mt-6 grid lg:grid-cols-2 gap-10">
        <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
          <img src={p.image} alt={p.name} className="w-full h-[380px] object-cover" />
        </div>
        <div>
          <div className="flex items-center gap-3">
            <span className="font-mono2 text-xs text-slate-400">{p.sku}</span>
            <DeliveryBadge mode={p.express_eligible ? "express" : "scheduled"} />
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-2">{p.name}</h1>
          <p className="mt-2 text-sm text-slate-600 flex items-center gap-2">
            Sold by <span className="font-semibold text-slate-900">{p.vendor_name}</span>
            {p.vendor_rating > 0 && <span className="inline-flex items-center gap-1 text-amber-600 font-semibold"><Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" />{p.vendor_rating}</span>}
          </p>
          <p className="mt-4 font-display text-3xl font-bold text-slate-900">{inr(p.price)}<span className="text-base font-normal text-slate-500"> / {p.unit}</span></p>
          <p className={`mt-2 text-sm font-semibold flex items-center gap-1.5 ${p.stock > 0 ? "text-emerald-600" : "text-red-600"}`}>
            <Package className="h-4 w-4" />{p.stock > 0 ? `${p.stock} ${p.unit} in stock` : "Out of stock"} · MOQ {p.moq} {p.unit}
          </p>

          <div className="mt-6 card-surface p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Standardized specifications</p>
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2" data-testid="spec-sheet">
              <div><dt className="text-[11px] text-slate-400">Category</dt><dd className="text-sm font-semibold text-slate-800 capitalize">{p.category}</dd></div>
              <div><dt className="text-[11px] text-slate-400">Brand</dt><dd className="text-sm font-semibold text-slate-800">{p.brand}</dd></div>
              {Object.entries(p.specs || {}).map(([k, v]) => (
                <div key={k}><dt className="text-[11px] text-slate-400 capitalize">{k.replace(/_/g, " ")}</dt><dd className="text-sm font-semibold text-slate-800">{v}</dd></div>
              ))}
            </dl>
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center rounded-lg border border-slate-300">
              <button data-testid="qty-minus" onClick={() => setQty(Math.max(p.moq, qty - 1))} className="p-2.5 text-slate-600 hover:bg-slate-50"><Minus className="h-4 w-4" /></button>
              <span className="w-12 text-center text-sm font-bold" data-testid="qty-value">{qty}</span>
              <button data-testid="qty-plus" onClick={() => setQty(qty + 1)} className="p-2.5 text-slate-600 hover:bg-slate-50"><Plus className="h-4 w-4" /></button>
            </div>
            <button data-testid="product-add-to-cart" disabled={p.stock <= 0} onClick={() => addToCart(p, qty)} className="btn-brand flex-1 !py-3">
              Add to cart · {inr(p.price * qty)}
            </button>
          </div>
          <p className="mt-4 text-xs text-slate-500 leading-relaxed">
            Exact ETA is computed at checkout based on your zone. If this SKU goes out of stock after ordering,
            you can opt into approved substitutes or an automatic line-item refund.
          </p>
        </div>
      </div>

      {p.similar?.length > 0 && (
        <div className="mt-12">
          <h2 className="font-display text-xl font-bold text-slate-900">Similar in {p.category}</h2>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {p.similar.map((s) => (
              <Link key={s.id} to={`/product/${s.id}`} data-testid={`similar-${s.sku}`} className="card-surface p-4 hover:shadow-md transition-shadow">
                <p className="font-mono2 text-[11px] text-slate-400">{s.sku}</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{s.name}</p>
                <p className="mt-1 text-xs text-slate-500">{s.brand} · {s.vendor_name}</p>
                <p className="mt-2 font-display font-bold text-slate-900">{inr(s.price)}<span className="text-xs font-normal text-slate-500">/{s.unit}</span></p>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
